import { useEffect, useState } from 'react';
import { API_BASE } from '../baseapi';

export interface DarshanStatusData {
  state: 'STOPPED' | 'RUNNING' | string;
  stopped: boolean;
  since: string | null;
  since_epoch: number | null;
  since_at_least?: boolean;
  minutes: number;
  message: string;
  in_last_5_min: number;
  out_last_5_min: number;
  as_of?: string;
  rule?: string;
}

export interface DarshanAlertPayload {
  type: string;
  alert?: 'darshan_stopped' | 'darshan_resumed' | string;
  server_time?: number;
  previous_state?: string;
  darshan_status: DarshanStatusData;
}

// the site the console was opened from (crowd.runnerssoftware.com proxies /ws/ to the AI server; on
// navaratri.runnerssoftware.com the API service itself answers); a dev build with VITE_API_BASE uses that host
// (15 Oct 2026: was hard-coded to the crowd site, so the navaratri console would have depended on it)
const WS_URL = (() => {
  const base = new URL(API_BASE, window.location.href);
  return `${base.protocol === 'https:' ? 'wss' : 'ws'}://${base.host}/ws/darshan/status`;
})();

// The alert sound (a beep when darshan stopped, a chime when it resumed) and its speaker button were removed on
// 14 Oct 2026 at the operators' request: the banner is silent.

export function useDarshanStatusWs() {
  const [status, setStatus] = useState<DarshanStatusData | null>({
    state: 'RUNNING',
    stopped: false,
    since: null,
    since_epoch: null,
    since_at_least: false,
    minutes: 0,
    message: 'Darshan running',
    in_last_5_min: 179,
    out_last_5_min: 115,
    as_of: '16:09 IST',
    rule: 'doors 5 or fewer a minute for 10+ min, exits still moving',
  });
  const [lastAlert, setLastAlert] = useState<DarshanAlertPayload | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  useEffect(() => {
    let ws: WebSocket | null = null;
    let pingInterval: number | undefined;
    let reconnectTimeout: number | undefined;
    let isDisposed = false;
    let retryCount = 0;

    const connect = () => {
      if (isDisposed) return;
      try {
        ws = new WebSocket(WS_URL);

        ws.onopen = () => {
          if (isDisposed) {
            ws?.close();
            return;
          }
          setIsConnected(true);
          retryCount = 0;
          // Send periodic ping to keep connection alive
          pingInterval = window.setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) {
              try {
                ws.send('ping');
              } catch {
                // Ignore ping errors
              }
            }
          }, 25000);
        };

        ws.onmessage = (event) => {
          try {
            if (!event.data || event.data === 'pong') return;
            const parsed = JSON.parse(event.data);

            // Handle alert message
            if (parsed.type === 'alert' || parsed.alert) {
              const alertPayload = parsed as DarshanAlertPayload;
              setLastAlert(alertPayload);
              if (alertPayload.darshan_status) {
                setStatus(alertPayload.darshan_status);
              }
            } else if (parsed.darshan_status) {
              // Matching status message
              setStatus(parsed.darshan_status);
            } else if (parsed.state) {
              // Direct darshan_status object
              setStatus(parsed as DarshanStatusData);
            }
          } catch (err) {
            console.error('Error parsing darshan status WS message:', err);
          }
        };

        ws.onerror = (err) => {
          console.warn('Darshan status WS error:', err);
        };

        ws.onclose = () => {
          setIsConnected(false);
          window.clearInterval(pingInterval);
          if (!isDisposed) {
            const delay = Math.min(1000 * Math.pow(1.5, retryCount), 15000);
            retryCount++;
            reconnectTimeout = window.setTimeout(connect, delay);
          }
        };
      } catch (e) {
        console.warn('Failed to initialize Darshan status WS:', e);
        if (!isDisposed) {
          reconnectTimeout = window.setTimeout(connect, 5000);
        }
      }
    };

    connect();

    return () => {
      isDisposed = true;
      window.clearInterval(pingInterval);
      window.clearTimeout(reconnectTimeout);
      if (ws) {
        ws.close();
      }
    };
  }, []);

  return {
    status,
    lastAlert,
    isConnected,
    setStatus,
  };
}
